import React, { useState, useRef } from "react";
import { Box, Divider, Drawer, IconButton, Typography } from "@mui/material";
import { ArrowBack, Close } from "@mui/icons-material";
import { Dialog } from "../../../components/Dialogs/Dialog";
import { QuickReplyTemplateListV2 } from "../QuickReplyTemplateListV2";
import { ManageQuickReplyTemplate } from "../ManageQuickReplyTemplate";
import { useMediaQuery, useTheme } from '@mui/material';
import { IQuickReplyTemplateData } from "../../../redux/quickReplyTemplates/quickReplyTemplate.types";
import { ILead } from "../../../redux/leads/leads.types";

interface ISelectQuickReplyTemplateProps {
  open: boolean;
  toNumber: string;
  onClose: () => void;
  leadData?: ILead;
  onSelectTemplate?: (
    message: string,
    quickReplyUuid?: string | null,
  ) => void;
}

export const SelectQuickReplyTemplate: React.FC<
  ISelectQuickReplyTemplateProps
> = (props) => {
  const { open, toNumber, onClose, onSelectTemplate, leadData } = props;
  const [searchTemplateName, setSearchTemplateName] = useState("");
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const refetchRef = useRef<(() => void) | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSelectTemplate = (message: string, template?: IQuickReplyTemplateData) => {
    if (onSelectTemplate) {
      onSelectTemplate(message, template?.quick_reply_uuid);
    }
    onClose();
  };

  const handleToggleCreatePanel = () => {
    setShowCreatePanel((prev) => !prev);
  };

  const handleSaveSuccess = () => {
    setShowCreatePanel(false);
    // Refetch the template list after successful creation
    if (refetchRef.current) {
      refetchRef.current();
    }
  };

  return (
    <Dialog
      size={isMobile ? "md" : "xl"}
      open={open}
      onClose={onClose}
      title={"Select Quick Reply Template"}
      titleSx={{
        typography: {
          fontSize: isMobile ? "1.1rem" : undefined,
          whiteSpace: isMobile ? "normal" : undefined,
          overflow: isMobile ? "visible" : undefined,
          textOverflow: isMobile ? "clip" : undefined,
          lineHeight: isMobile ? "1.3" : undefined,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 3,
          minHeight: "60vh",
          maxHeight: "75vh",
          overflow: "hidden",
        }}
      >
        {/* Left Panel - Template List */}
        <Box
          sx={{
            width: isMobile ? "100%" : showCreatePanel ? "38%" : "100%",
            flexShrink: 0,
            overflow: "auto",
            transition: "width 0.25s ease-out",
          }}
        >
          <QuickReplyTemplateListV2
            selectedTemplateUuid={null}
            showCreateButton={false}
            searchTemplateName={searchTemplateName}
            onSearchChange={setSearchTemplateName}
            toNumber={toNumber}
            isFromChat={true}
            showCreatePanel={showCreatePanel}
            onClose={onClose}
            onCreateClick={handleToggleCreatePanel}
            hideCreateButton={showCreatePanel}
            compactCardsView={showCreatePanel}
            refetchRef={refetchRef}
            onSelectTemplate={handleSelectTemplate}
            hideEditIcon={true}
          />
        </Box>

        {/* Right Panel - Create Template Form */}
        {showCreatePanel && !isMobile && (
          <Box
            sx={{
              width: "70%",
              // flexShrink: 0,
              // overflow: "auto",
              borderLeft: "1px solid",
              borderColor: "divider",
              pl: 3,
              // position: "relative",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
                position: "sticky",
                top: 0,
                backgroundColor: "background.paper",
                zIndex: 1,
                pb: 1,
              }}
            >
              <Typography variant="h5" fontWeight={600}>
                Create Quick Reply Template
              </Typography>
              <IconButton
                size="small"
                onClick={handleToggleCreatePanel}
                sx={{ color: "text.secondary" }}
              >
                <Close />
              </IconButton>
            </Box>
            <ManageQuickReplyTemplate
              fromRightPanel
              onSaveSuccess={handleSaveSuccess}
              templateData={undefined}
            />
          </Box>
        )}
      </Box>

      {isMobile && (
        <Drawer
          anchor="bottom"
          open={showCreatePanel}
          onClose={handleToggleCreatePanel}
          sx={{
            zIndex: (theme) => theme.zIndex.modal + 5,
          }}
          PaperProps={{
            sx: {
              height: "100dvh",
              borderRadius: 0,
              zIndex: (theme) => theme.zIndex.modal + 6,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1,
              py: 1,
              bgcolor: "primary.main",
              color: "#fff",
            }}
          >
            <IconButton onClick={handleToggleCreatePanel} sx={{ color: "#fff" }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" sx={{ flex: 1, ml: 1 }}>
              Create Template
            </Typography>
            <IconButton onClick={handleToggleCreatePanel} sx={{ color: "#fff" }}>
              <Close />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography variant="h5" fontWeight={600}>
                Create Quick Reply Template
              </Typography>
              <IconButton
                size="small"
                onClick={handleToggleCreatePanel}
                sx={{ color: "text.secondary" }}
              >
                <Close />
              </IconButton>
            </Box>
            <ManageQuickReplyTemplate
              fromRightPanel
              onSaveSuccess={handleSaveSuccess}
              templateData={undefined}
            />
          </Box>
        </Drawer>
      )}

    </Dialog>
  );
};
